import React, { useMemo } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  GlobalStyles,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  QUOTE_STATUS,
  calcTotals,
  formatDate,
  formatMoney,
  resolveQuoteStatus,
  resolveVatPercentage,
} from "../quoteUtils";
import CurrencySymbol from "../../common/CurrencySymbol";

const statusColor = (status) => {
  if (status === QUOTE_STATUS.APPROVED) return { bg: "#ecfdf5", color: "#047857" };
  if (status === QUOTE_STATUS.REJECTED) return { bg: "#fef2f2", color: "#b91c1c" };
  if (status === QUOTE_STATUS.EXPIRED) return { bg: "#fff7ed", color: "#9a3412" };
  return { bg: "#f3f4f6", color: "#374151" };
};

const navy = "#1a1d4f";
const green = "#00c179";
const line = "#e2e9ef";
const muted = "#6b7280";

const asText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return (
      value.label ||
      value.name ||
      value.key ||
      value.modelName ||
      value.value ||
      ""
    );
  }
  return String(value);
};

const QuoteChrome = ({ quoteNumber, accountName }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      zIndex: 5,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 1.5,
      px: 2.25,
      py: 1.25,
      bgcolor: "#fff",
      borderBottom: `1px solid ${line}`,
      "@media print": { display: "none" },
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: navy }}>
      {quoteNumber} · {accountName || ""}
    </Typography>
    <Stack direction="row" spacing={1}>
      <Button
        variant="contained"
        size="small"
        onClick={() => window.print()}
        sx={{
          bgcolor: green,
          textTransform: "none",
          fontWeight: 600,
          "&:hover": { bgcolor: "#00a868" },
        }}
      >
        Print / PDF
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => window.close()}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          color: navy,
          borderColor: line,
        }}
      >
        Close
      </Button>
    </Stack>
  </Box>
);

const QuoteHeader = ({ status }) => {
  const resolvedStatus = status || QUOTE_STATUS.DRAFT;
  const colors = statusColor(resolvedStatus);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        pt: 2.75,
        pb: 2,
        borderBottom: `4px solid ${green}`,
      }}
    >
      <Box component="img" src="logo.png" alt="logo" sx={{ width: 180 }} />
      <Box sx={{ textAlign: "right" }}>
        <Typography sx={{ m: 0, fontSize: 18, fontWeight: 600, color: navy }}>
          عرض سعر
        </Typography>
        <Typography sx={{ m: 0, fontSize: 26, fontWeight: 700, color: navy }}>
          SERVICE QUOTATION
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-block",
            mt: 0.75,
            px: 1.25,
            py: 0.35,
            borderRadius: "4px",
            bgcolor: colors.bg,
            color: colors.color,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "capitalize",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          }}
        >
          {resolvedStatus}
        </Box>
      </Box>
    </Box>
  );
};

const QuoteCompany = ({ company }) => (
  <Grid container spacing={2} sx={{ px: 3, py: 2 }}>
    <Grid item xs={12} sm={6}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
        {company?.companyName || "Bayanat Smart Systems LLC"}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>{company?.addressEng1 || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>{company?.addressEng2 || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>VAT: {company?.vatNumber || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>Phone: {company?.phone || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>{company?.email || ""}</Typography>
    </Grid>
    <Grid item xs={12} sm={6} sx={{ textAlign: "right", direction: "rtl" }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
        {company?.companyNameArb || "شركة باينات للأنظمة الذكية ذ.م.م"}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>{company?.addressArb1 || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>{company?.addressArb2 || ""}</Typography>
      <Typography sx={{ fontSize: 12 }}>
        الرقم الضريبي:{" "}
        <Box
          component="span"
          dir="ltr"
          sx={{ display: "inline-block", unicodeBidi: "isolate" }}
        >
          {company?.vatNumber || ""}
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        الهاتف:{" "}
        <Box
          component="span"
          dir="ltr"
          sx={{ display: "inline-block", unicodeBidi: "isolate" }}
        >
          {company?.phone || ""}
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        <Box
          component="span"
          dir="ltr"
          sx={{ display: "inline-block", unicodeBidi: "isolate" }}
        >
          {company?.email || ""}
        </Box>
      </Typography>
    </Grid>
  </Grid>
);

const QuoteMeta = ({ quote }) => {
  const items = [
    { label: "Quote No", value: asText(quote.quoteNumber || quote.quoteRefNo) || "-" },
    { label: "Quote Ref No", value: asText(quote.quoteRefNo || quote.quoteNumber) || "-" },
    { label: "Quote Date", value: formatDate(quote.quoteDate) },
    { label: "Printed", value: moment().format("DD MMM YYYY HH:mm") },
    { label: "Valid until", value: formatDate(quote.expiryDate) },
    {
      label: "Sales Manager",
      value:
        asText(quote.salesPersonName) ||
        asText(quote.attributes?.salePersonName) ||
        "-",
    },
  ];
  return (
    <Grid container spacing={1} sx={{ px: 3, pb: 2 }}>
      {items.map((item) => (
        <Grid item xs={6} sm key={item.label}>
          <Typography sx={{ fontSize: 11, color: muted }}>
            {item.label}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {item.value}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );
};

const QuoteParties = ({ quote }) => {
  const make = asText(quote.make);
  const model = asText(quote.model || quote.modelId);
  const year = asText(quote.year);
  const fuelType = asText(quote.fuelType);
  const vehicleType = asText(quote.vehicleType);
  const plate =
    asText(quote.plate) || asText(quote.attributes?.licensePlate) || "";
  const vin = asText(quote.vin);
  const hasVehicle = Boolean(
    quote.vehicleId ||
      vin ||
      plate ||
      make ||
      model ||
      year ||
      fuelType ||
      vehicleType,
  );

  return (
    <Grid container spacing={2} sx={{ px: 3, pb: 2 }}>
      <Grid item xs={12} sm={hasVehicle ? 6 : 12}>
        <Paper variant="outlined" sx={{ p: 1.5, borderColor: line }}>
          <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 700, color: navy }}>
            Customer details
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
            {asText(quote.accountName)}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            Contact: {asText(quote.contactPerson || quote.customerName)}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            Mobile: {asText(quote.mobile || quote.contactPersonMobile)}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            Email: {asText(quote.email)}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            Address: {asText(quote.address)}
          </Typography>
          <Typography sx={{ fontSize: 12 }}>
            VAT: {asText(quote.vatNumber)}
          </Typography>
        </Paper>
      </Grid>
      {hasVehicle ? (
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: line }}>
            <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 700, color: navy }}>
              Vehicle details
            </Typography>
            <Typography sx={{ fontSize: 12 }}>VIN: {vin}</Typography>
            <Typography sx={{ fontSize: 12 }}>Plate: {plate}</Typography>
            <Typography sx={{ fontSize: 12 }}>
              {[make, model, year].filter(Boolean).join(" ")}
            </Typography>
            <Typography sx={{ fontSize: 12 }}>Fuel: {fuelType}</Typography>
            <Typography sx={{ fontSize: 12 }}>Type: {vehicleType}</Typography>
          </Paper>
        </Grid>
      ) : null}
    </Grid>
  );
};

const QuoteItemsTable = ({ items = [], vatPercentage = 15 }) => (
  <Table
    size="small"
    sx={{
      width: "calc(100% - 48px)",
      mx: 3,
      mb: 2,
      "& th": {
        bgcolor: navy,
        color: "#fff",
        fontWeight: 700,
        fontSize: 12,
        py: 1,
        px: 1,
      },
      "& td": {
        borderBottom: `1px solid ${line}`,
        fontSize: 12,
        py: 1,
        px: 1,
      },
    }}
  >
    <TableHead>
      <TableRow>
        <TableCell>#</TableCell>
        <TableCell>Type</TableCell>
        <TableCell>Description</TableCell>
        <TableCell>Qty</TableCell>
        <TableCell>Unit</TableCell>
        <TableCell>VAT</TableCell>
        <TableCell>Amount</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {items.map((item, index) => (
        <TableRow key={index}>
          <TableCell>{index + 1}</TableCell>
          <TableCell>{item.serviceType}</TableCell>
          <TableCell>
            <strong>{item.itemName}</strong>
            {item.description ? <Box>{item.description}</Box> : null}
          </TableCell>
          <TableCell>{item.quantity}</TableCell>
          <TableCell>{formatMoney(item.unitPrice)}</TableCell>
          <TableCell>
            {item.withVat ? `${Math.round(vatPercentage)}%` : "—"}
          </TableCell>
          <TableCell>{formatMoney(item.lineTotal)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const QuoteTotals = ({ totals, currencyLabel }) => (
  <Stack spacing={0.75} sx={{ width: 280, ml: "auto", mr: 3, mb: 2, fontSize: 13 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <span>Subtotal</span>
      <strong>{formatMoney(totals.subtotal)}</strong>
    </Box>
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <span>VAT</span>
      <strong>{formatMoney(totals.vatTotal)}</strong>
    </Box>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        fontWeight: 700,
        color: "#067a4c",
        borderTop: `1px solid ${line}`,
        pt: 1,
      }}
    >
      <span>
        Grand Total{" "}
        <CurrencySymbol currency={currencyLabel} withParentheses={false} />
      </span>
      <strong>{formatMoney(totals.grandTotal)}</strong>
    </Box>
  </Stack>
);

const QuoteTerms = ({ termsEnglish, termsArabic }) => (
  <Box sx={{ px: 3, pb: 2 }}>
    <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 700, color: navy }}>
      Terms and conditions / الشروط والأحكام
    </Typography>
    <Typography sx={{ fontSize: 11, color: "#444", lineHeight: 1.5 }}>
      {termsEnglish}
    </Typography>
    <Typography
      sx={{ fontSize: 11, color: "#444", lineHeight: 1.5, textAlign: "right", direction: "rtl" }}
    >
      {termsArabic}
    </Typography>
  </Box>
);

const QuoteSignatures = () => (
  <Grid container spacing={5} sx={{ px: 3, pb: 2, mt: 1 }}>
    <Grid item xs={12} sm={6}>
      <Typography sx={{ borderTop: "1px solid #222", pt: 1, fontSize: 12 }}>
        Customer signature / توقيع العميل
      </Typography>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Typography sx={{ borderTop: "1px solid #222", pt: 1, fontSize: 12 }}>
        Sales manager / مدير المبيعات
      </Typography>
    </Grid>
  </Grid>
);

const QuoteSheet = ({ quote, company, totals, currencyLabel }) => (
  <Box sx={{ p: { xs: 1.5, sm: "28px 12px 48px" }, "@media print": { p: 0 } }}>
    <Paper
      elevation={0}
      sx={{
        width: 900,
        maxWidth: "95%",
        mx: "auto",
        bgcolor: "#fff",
        border: `1px solid ${line}`,
        borderRadius: 0,
        overflow: "hidden",
        "@media print": { width: "100%", maxWidth: "none", border: 0 },
      }}
    >
      <QuoteHeader status={resolveQuoteStatus(quote)} />
      <QuoteCompany company={company} />
      <Chip
        label="This document is a quotation only — not a tax invoice / هذه الوثيقة عرض سعر وليست فاتورة ضريبية"
        sx={{
          ml: 3,
          mb: 1.5,
          height: "auto",
          py: 0.5,
          borderRadius: "6px",
          bgcolor: "#fff7ed",
          color: "#9a3412",
          fontSize: 11,
          fontWeight: 700,
          "& .MuiChip-label": { whiteSpace: "normal" },
        }}
      />
      <QuoteMeta quote={quote} />
      <QuoteParties quote={quote} />
      <QuoteItemsTable
        items={quote.items}
        vatPercentage={resolveVatPercentage(
          quote.vatPercentage ?? quote.account,
        )}
      />
      <QuoteTotals totals={totals} currencyLabel={currencyLabel} />
      <QuoteTerms
        termsEnglish={quote.termsEnglish}
        termsArabic={quote.termsArabic}
      />
      <QuoteSignatures />
    </Paper>
  </Box>
);

const QuotationDetailView = ({ quote, company, emptyText }) => {
  const logInUser = useSelector((state) => state.logInUsers);
  const currencyLabel = logInUser?.attributes?.currencyLabel || "AED";
  const totals = useMemo(
    () => calcTotals(quote?.items || []),
    [quote?.items],
  );
  if (!quote) {
    return (
      <Typography sx={{ py: 10, px: 2.5, textAlign: "center" }}>
        {emptyText || "Quotation not found"}
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#e8ecef",
        fontFamily: '"Open Sans", sans-serif',
        color: "#222",
        "@media print": { bgcolor: "#fff" },
      }}
    >
      <GlobalStyles
        styles={{
          "*": {
            boxSizing: "border-box",
            WebkitPrintColorAdjust: "exact",
            printColorAdjust: "exact",
          },
          "@media print": {
            "html, body, #root": {
              background: "#fff !important",
              backgroundColor: "#fff !important",
            },
          },
        }}
      />
      <QuoteChrome
        quoteNumber={asText( quote.quoteNumber||quote.quoteRefNo)}
        accountName={asText(quote.accountName)}
      />
      <QuoteSheet
        quote={quote}
        company={company}
        totals={totals}
        currencyLabel={currencyLabel}
      />
    </Box>
  );
};

export default QuotationDetailView;
